/**
 * Local testbed support (CAP-951).
 *
 * The testbed runs admin-api on a copy of production data behind an identity proxy instead of
 * Cloudflare Access. admin-ui meets it through `npm run dev:testbed`, which loads `.env.testbed`
 * (`VITE_TESTBED=1`). The flag is explicit on purpose: it is never inferred from the hostname, so
 * a production build can only turn it on by someone setting the variable.
 *
 * Contract: admin-api `docs/local-testbed-interfaces.md`, section 4.8 (`/cdn-cgi/access/get-identity`
 * on the proxy) and section 4.7 (`/__env`).
 */

export function isTestbed(): boolean {
  const flag = import.meta.env.VITE_TESTBED
  return flag === '1' || flag === 'true'
}

/** The identity proxy's origin, without a trailing slash. */
export function getTestbedProxyUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL
  return typeof url === 'string' ? url.trim().replace(/\/+$/, '') : ''
}

export interface TestbedIdentity {
  email?: string
  name?: string
  /** `admin` or `portal`: the kind the testbed identity was signed in as. */
  testbed?: { kind?: string }
}

/** The proxy answered 401: nobody is signed in on the testbed. */
export class TestbedNotSignedInError extends Error {
  readonly signInUrl: string

  constructor(signInUrl: string) {
    super('Not signed in to the testbed')
    this.name = 'TestbedNotSignedInError'
    this.signInUrl = signInUrl
  }
}

/** Any other failure to ask the proxy who you are (proxy down, refused origin, bad answer). */
export class TestbedProxyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TestbedProxyError'
  }
}

/**
 * Ask the testbed proxy who the browser is signed in as. Cloudflare's `get-identity` does not
 * exist on the Vite dev server's own origin, so this goes to the proxy (the API base URL), with
 * the `testbed_identity` cookie.
 */
export async function fetchTestbedIdentity(): Promise<TestbedIdentity> {
  const proxy = getTestbedProxyUrl()
  if (!proxy) {
    throw new TestbedProxyError('VITE_API_BASE_URL is not set, so the testbed proxy is unknown')
  }

  let response: Response
  try {
    response = await fetch(`${proxy}/cdn-cgi/access/get-identity`, { credentials: 'include' })
  } catch {
    throw new TestbedProxyError(`Cannot reach the testbed proxy at ${proxy}`)
  }

  if (response.status === 401) {
    let signin: unknown
    try {
      signin = ((await response.json()) as { signin?: unknown } | null)?.signin
    } catch {
      signin = undefined
    }
    // `signin` is a path on the proxy; never follow an absolute URL from a response body.
    const path = typeof signin === 'string' && signin.startsWith('/') ? signin : '/__signin'
    throw new TestbedNotSignedInError(`${proxy}${path}`)
  }

  if (!response.ok) {
    throw new TestbedProxyError(`The testbed proxy answered ${response.status} for the identity`)
  }

  try {
    return (await response.json()) as TestbedIdentity
  } catch {
    throw new TestbedProxyError('The testbed proxy sent an identity that is not JSON')
  }
}

export interface TestbedEnvironment {
  name: string
  slot?: number
  snapshot?: { id?: string; created_at?: string; age_h?: number } | null
  started_at?: string
}

/**
 * The environment's own name and the age of its data. `/__env` sends no CORS headers, so the Vite
 * dev server forwards this path to the proxy (`vite.config.js`, testbed mode only) and the request
 * stays on the page's origin.
 */
export async function fetchTestbedEnvironment(): Promise<TestbedEnvironment> {
  const response = await fetch(new URL('/__env', window.location.origin))
  if (!response.ok) {
    throw new TestbedProxyError(`The testbed proxy answered ${response.status} for /__env`)
  }
  const body = (await response.json()) as TestbedEnvironment | null
  if (!body || typeof body.name !== 'string') {
    throw new TestbedProxyError('The testbed proxy sent no environment')
  }
  return body
}
